import { getRelativeSelector } from "@/helpers/utility/DOM";

/**
 * @param node
 */
export function getMaxCaretIndex(node: Node) {
  if (isTextNode(node)) {
    return node.nodeValue?.length || 0;
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    return node.childNodes.length;
  }
  return 0;
}

/**
 * Return the next element sibling of the element the caret is currently in.
 * @param range
 */
export function getNodeBeforeRange(range: Range | RangeCopy) {
  if (isTextNode(range.startContainer)) {
    return range.startContainer.parentElement?.previousSibling || null;
  }
  if (range.startOffset > 0) {
    const childNode = range.startContainer.childNodes[range.startOffset - 1];
    if (childNode.textContent != "") {
      return range.startContainer.childNodes[range.startOffset - 1];
    }
  }
  if (range.startContainer.previousSibling) {
    return range.startContainer.previousSibling;
  }

  return null;
}

/**
 * Return the next element sibling of the element the caret is currently in.
 * @param range
 */
export function getNodeAfterRange(range: Range | RangeCopy) {
  if (isTextNode(range.endContainer)) {
    return range.endContainer.parentElement?.nextSibling || null;
  }
  const nodeMaxEndOffset = range.endContainer.childNodes.length - 1;
  if (range.endOffset < nodeMaxEndOffset) {
    const childNode = range.endContainer.childNodes[range.endOffset + 1];
    if (childNode.textContent != "") {
      return range.endContainer.childNodes[range.endOffset + 1];
    }
  }
  if (range.endContainer.nextSibling) {
    return range.endContainer.nextSibling;
  }

  return null;
}

/**
 * Return the next element sibling of the element the caret is currently in.
 * @param range
 */
export function getElementAfterRange(range: Range | RangeCopy) {
  const endContainer = range.endContainer as Node | HTMLElement;
  if ("nextElementSibling" in endContainer && endContainer.nextElementSibling) {
    return endContainer.nextElementSibling;
  }
  if (isTextNode(endContainer)) {
    return endContainer.parentElement?.nextElementSibling || null;
  }
  return null;
}

/**
 * Return the selection range.
 * @param baseElement
 * @param asCopy - Return a copy of the range instead of the actual range.
 */
export function getCurrentSelectionRange<C extends boolean>(
  baseElement: HTMLElement,
  asCopy: C = false as C
) {
  type Result = C extends true ? RangeCopy | null : Range | null;
  const selection = window.getSelection();
  if (!selection) {
    return null as Result;
  }
  if (selection.rangeCount === 0) {
    return null as Result;
  }
  const range = selection.getRangeAt(0);
  if (!range) return null as Result;

  if (asCopy) {
    return createRangeCopy(range, baseElement) as Result;
  }
  return range.cloneRange() as Result;
}

/**
 * Find a number in a range, return the number and the range.
 * @param baseElement
 */
export function findFirstNumberInRange(baseElement: HTMLElement | null = null) {
  if (!baseElement) return null;
  const range = getCurrentSelectionRange(baseElement, false);
  if (!range) return null;

  // get the position of the first number in the range:
  let position = 0;
  const rangedText = range.toString();

  const numberRegex = /[-]?[\d]+([.][\d]+)?/;
  const match = numberRegex.exec(rangedText);
  if (match) {
    position = match.index;
  }

  const isNumberRegex = /^[-+]?([\d]+([.][\d]*)?)?$/;
  let digitsRange = setRangeToPosition(range, position);

  let next = expandRange(digitsRange, 1);
  let prev = expandRange(digitsRange, -1);
  let nextNumber = digitsRange.toString() + next.letter;
  let possibleNextNumber = prev.letter.slice(-1) + nextNumber;
  while (
    next.letter != "" &&
    (isNumberRegex.test(nextNumber) || isNumberRegex.test(possibleNextNumber))
  ) {
    digitsRange = next.range;
    next = expandRange(digitsRange, 1);
    nextNumber = digitsRange.toString() + next.letter;
    possibleNextNumber = prev.letter.slice(-1) + nextNumber;
  }

  prev = expandRange(digitsRange, -1);
  let prePrev = expandRange(prev.range, -1);
  nextNumber = prev.letter + digitsRange.toString();
  possibleNextNumber = prePrev.letter + nextNumber;
  while (
    prev.letter != "" &&
    (isNumberRegex.test(nextNumber) || isNumberRegex.test(possibleNextNumber))
  ) {
    digitsRange = prev.range;
    prev = expandRange(digitsRange, -1);
    nextNumber = prev.letter + digitsRange.toString();
    prePrev = expandRange(prev.range, -1);
    possibleNextNumber = prePrev.letter + nextNumber;
  }

  let numberString = digitsRange.toString();

  const numberLength = numberString.length;

  if (!numberString || !/^[-+]?[\d]+([.][\d]*)?$/.test(numberString)) {
    return null;
  }

  const hasOperator = /(^|[\d)}])$/.test(prev.letter);

  if (numberString.startsWith("+")) {
    numberString = numberString.substring(1);
  }

  return {
    number: numberString,
    range: digitsRange,
    numberLength,
    hasOperator: hasOperator
  };
}

/**
 * @param range - the range to expand
 * @param dir - the direction to expand the range in
 */
export function expandRange(range: Range | RangeCopy, dir = 1) {
  const { startContainer, startOffset, endContainer, endOffset } = range;

  const { node: textStartContainer, offset: textStartOffset } =
    getTextNodeAtOffset(startContainer, startOffset);
  const { node: textEndContainer, offset: textEndOffset } = getTextNodeAtOffset(
    endContainer,
    endOffset
  );
  const endLimit = textEndContainer?.nodeValue?.length ?? 0;

  let newLetter = "";
  const newRange = document.createRange();
  const combinedRange = document.createRange();

  if (dir < 0) {
    newRange.setEnd(textStartContainer, textStartOffset);
    if (textStartOffset > 0) {
      newRange.setStart(textStartContainer, textStartOffset - 1);
    } else {
      const prevElement = getNodeBeforeRange(range);
      if (prevElement) {
        if (prevElement.nodeName === "DIV") {
          prevElement.previousSibling;
        }

        if (isTextNode(prevElement)) {
          newRange.setStart(prevElement, prevElement.data.length - 1);
        } else {
          const { node: textContentElement } = getTextNodeAtOffset(
            prevElement,
            prevElement.childNodes.length
          );
          const end = Math.max(
            isTextNode(textContentElement)
              ? textContentElement.data.length - 1
              : textContentElement.childNodes.length - 1,
            0
          );
          newRange.setStart(textContentElement, end);
        }
      } else {
        newRange.setStart(textStartContainer, textStartOffset);
      }
    }
    newLetter = newRange.toString();
    combinedRange.setStart(newRange.startContainer, newRange.startOffset);
    combinedRange.setEnd(range.endContainer, range.endOffset);
  } else {
    newRange.setStart(textEndContainer, textEndOffset);
    if (textEndOffset < endLimit) {
      newRange.setEnd(textEndContainer, textEndOffset + 1);
    } else {
      const nextElement = getElementAfterRange(range);
      if (nextElement) {
        if (nextElement.tagName === "DIV") {
          nextElement.nextElementSibling;
        }

        if (nextElement.tagName === "SPAN") {
          const textContentElement = nextElement.lastChild;
          if (textContentElement) {
            newRange.setEnd(textContentElement, 1);
          }
        } else {
          newRange.setEnd(nextElement, 1);
        }
      } else {
        newRange.setEnd(endContainer, endOffset);
      }
    }
    newLetter = newRange.toString();
    combinedRange.setStart(range.startContainer, range.startOffset);
    combinedRange.setEnd(newRange.endContainer, newRange.endOffset);
  }

  return {
    letter: newLetter,
    range: combinedRange
  };
}

/**
 *
 * @param container
 * @param offset
 */
function getTextNodeAtOffset(
  container: Node,
  offset = 0
): { node: Node; offset: number } {
  if (isTextNode(container)) {
    return { node: container, offset };
  }

  // try getting a node with content first
  const contentNodeOffset = offset;
  let lastWithContent = null;
  for (let i = 0; i < container.childNodes.length; i++) {
    const childNode = container.childNodes[i];
    if (childNode.textContent == "") {
      continue;
    }
    if (contentNodeOffset <= i) {
      return getTextNodeAtOffset(childNode, 0);
    }
    lastWithContent = childNode;
  }

  // didn't find the node at the offset, but
  // found a node with content?
  // take that.
  if (lastWithContent) {
    const childOffset = isTextNode(lastWithContent)
      ? lastWithContent.data.length
      : lastWithContent.childNodes.length;
    return getTextNodeAtOffset(lastWithContent, childOffset);
  }

  if (container.childNodes[offset]) {
    return getTextNodeAtOffset(container.childNodes[offset], 0);
  }
  if (container.childNodes[offset - 1]) {
    const childNode = container.childNodes[offset - 1];
    const childOffset = isTextNode(childNode)
      ? childNode.data.length
      : childNode.childNodes.length;
    return getTextNodeAtOffset(childNode, childOffset);
  }

  return { node: container, offset: offset };
}

/**
 * Move the caret after an element node.
 * @param elementNode
 */
export function moveCaretAfter(elementNode: Element) {
  if (elementNode instanceof HTMLElement) {
    elementNode.scrollIntoView();
  } else if (elementNode.parentElement) {
    elementNode.parentElement.scrollIntoView();
  }

  // reset the selection
  const newRange = document.createRange();
  const nextElement = elementNode.nextElementSibling;
  if (nextElement) {
    newRange.setStart(nextElement, 0);
    newRange.setEnd(nextElement, 0);
  }

  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(newRange);
  }
}

/**
 * @param range
 * @param position
 */
export function setRangeToPosition<T extends Range | RangeCopy>(
  range: T,
  position: number
) {
  const text = range.toString();
  if (position <= 0) {
    return range;
  }
  if (position > text.length) {
    return range;
  }
  const newRange = document.createRange();
  if (range.startContainer == range.endContainer) {
    newRange.setStart(range.startContainer, range.startOffset + position);
    newRange.setStart(range.endContainer, range.startOffset + position);
  }
  let el = range.startContainer;
  let rest = position;
  let offset = range.startOffset;
  while (rest > (el.textContent?.length || 0) - offset) {
    rest = rest - ((el.textContent?.length || 0) - offset);
    offset = 0;
    if (el.nextSibling == null) break;
    el = el.nextSibling;
  }
  newRange.setStart(el, offset + rest);
  newRange.collapse(true);
  return newRange;
}

export type RangeCopy = {
  startOffset: number;
  startContainer: Node;
  startContainerQuery: string;
  startContainerIndex: number;
  startContainerTextIndex: number;
  endOffset: number;
  endContainer: Node;
  endContainerQuery: string;
  endContainerIndex: number;
  endContainerTextIndex: number;
  commonAncestorContainer: Node;
  startTextContainer: Node;
  startTextOffset: number;
  endTextContainer: Node;
  endTextOffset: number;
  currentInput?: Element;
  refreshed: (currentInput: Element) => RangeCopy;
};

/**
 * Create a static copy of a range that can be used to restore
 * the range even if the html changed in the meantime.
 * @param range
 * @param baseElement
 */
export function createRangeCopy(
  range: Range | RangeCopy,
  baseElement: HTMLElement
) {
  const input = baseElement;

  const startContainer = range.startContainer;
  const startContainerElement = isTextNode(startContainer)
    ? startContainer.parentElement
    : startContainer;

  let startContainerQuery;
  let startContainerIndex = -1;
  let startContainerTextIndex = -1;
  if (startContainer == input) {
    startContainerQuery = "";
  } else {
    startContainerQuery = getRelativeSelector(startContainer, input);
    if (startContainerQuery.trim() != "") {
      startContainerIndex = Array.prototype.indexOf.call(
        input.querySelectorAll(startContainerQuery),
        startContainerElement
      );
    }

    if (isTextNode(startContainer)) {
      startContainerTextIndex = Array.prototype.indexOf.call(
        startContainer.parentElement?.childNodes || [],
        startContainer
      );
    }
  }

  const endContainer = range.endContainer;
  const endContainerElement = isTextNode(endContainer)
    ? endContainer.parentElement
    : endContainer;

  let endContainerQuery;
  let endContainerIndex = -1;
  let endContainerTextIndex = -1;
  if (endContainer == input) {
    endContainerQuery = "";
  } else {
    endContainerQuery = getRelativeSelector(endContainer, input);
    if (endContainerQuery.trim() != "") {
      endContainerIndex = Array.prototype.indexOf.call(
        input.querySelectorAll(endContainerQuery),
        endContainerElement
      );
    }

    if (isTextNode(endContainer)) {
      endContainerTextIndex = Array.prototype.indexOf.call(
        endContainer.parentElement?.childNodes || [],
        endContainer
      );
    }
  }

  const { node: startTextContainer, offset: startTextOffset } =
    getTextNodeAtOffset(startContainer, range.startOffset);
  const { node: endTextContainer, offset: endTextOffset } = getTextNodeAtOffset(
    endContainer,
    range.endOffset
  );

  const rangeCopy: RangeCopy = {
    startOffset: range.startOffset,
    startContainer,
    startContainerQuery,
    startContainerIndex,
    startContainerTextIndex,
    endOffset: range.endOffset,
    endContainer,
    endContainerQuery,
    endContainerIndex,
    endContainerTextIndex,
    commonAncestorContainer: range.commonAncestorContainer,

    startTextContainer,
    startTextOffset,
    endTextContainer,
    endTextOffset,

    refreshed(currentInput: Element): RangeCopy {
      // GET START CONTAINER

      let startOffset = this.startOffset;
      let startContainer = this.startContainer;
      let startContainerIndex = this.startContainerIndex;

      if (this.startContainerQuery === "") {
        startContainer = currentInput;
      } else {
        // select the nearest element to the startContainer
        const startContainerFits = currentInput.querySelectorAll(
          this.startContainerQuery
        );
        startContainer = startContainerFits[startContainerIndex];
        while (startContainer == null && startContainerIndex >= 0) {
          startContainerIndex--;
          startContainer = startContainerFits[startContainerIndex];
          startOffset = startContainer?.childNodes?.length ?? 0;

          // select end of text node if there's text content in the node:
          if (startContainer && (startContainer.textContent?.length || 0) > 0) {
            startContainer =
              startContainer.childNodes[startContainer.childNodes.length - 1];
            startOffset = startContainer?.textContent?.length ?? 0;
          }
        }

        // select the text node if start container was text node
        if (
          startContainer &&
          isTextNode(this.startContainer) &&
          !isTextNode(startContainer)
        ) {
          const textNodesParent = startContainer;
          let index = this.startContainerTextIndex;
          const childNodes = textNodesParent?.childNodes;
          if (childNodes.length == 0) {
            const textNode = document.createTextNode("");
            startContainer.appendChild(textNode);
            startContainer = textNode;
            startOffset = 0;
          } else {
            startContainer = childNodes[index];
            while (startContainer == null && index >= 0) {
              index--;
              startContainer = textNodesParent?.childNodes[index];
              startOffset = startContainer?.textContent?.length ?? 0;
            }
          }
        }
      }

      // GET END CONTAINER

      let endOffset = this.endOffset;
      let endContainer = this.endContainer;
      let endContainerIndex = this.endContainerIndex;

      if (this.endContainerQuery === "") {
        endContainer = currentInput;
      } else {
        // select the nearest element to the endContainer
        const endContainerFits = currentInput.querySelectorAll(
          this.endContainerQuery
        );
        endContainer = endContainerFits[endContainerIndex];
        while (endContainer == null && endContainerIndex >= 0) {
          endContainerIndex--;
          endContainer = endContainerFits[endContainerIndex];
          endOffset = endContainer?.childNodes?.length ?? 0;

          // select end of text node if there's text content in the node:
          if (endContainer && (endContainer.textContent?.length || 0) > 0) {
            endContainer =
              endContainer.childNodes[endContainer.childNodes.length - 1];
            endOffset = endContainer?.textContent?.length ?? 0;
          }
        }

        // select the text node if start container was text node
        if (endContainer && isTextNode(this.endContainer)) {
          const textNodesParent = endContainer;
          let index = this.endContainerTextIndex;
          endContainer = textNodesParent?.childNodes[index];
          while (endContainer == null && index >= 0) {
            index--;
            endContainer = textNodesParent?.childNodes[index];
            endOffset = endContainer?.textContent?.length ?? 0;
          }
        }
      }

      const returnValue: RangeCopy = {
        ...this,
        startOffset,
        startContainer,
        endOffset,
        endContainer,
        currentInput
      };
      return returnValue;
    }
  };

  return rangeCopy;
}

/**
 * Get the next element sibling of the element until the sibling is editable.
 * @param element
 * @param maxParent
 */
export function getNextEditableElementSibling(
  element: Node,
  maxParent: HTMLElement | null = null
) {
  let nextElement = getNextElementSibling(element, maxParent);
  while (
    nextElement &&
    (!("isContentEditable" in nextElement) ||
      nextElement.isContentEditable === false)
  ) {
    nextElement = getNextElementSibling(nextElement, maxParent);
  }
  return nextElement;
}

/**
 * @param node
 * @param maxParent
 */
export function getNextElementSibling(
  node: Node | HTMLElement,
  maxParent: HTMLElement | null = null
): Element | HTMLElement | null {
  if (isTextNode(node)) {
    return node.parentElement?.nextElementSibling || null;
  }
  if ("nextElementSibling" in node && node.nextElementSibling) {
    return node.nextElementSibling;
  }
  if (node.parentElement && node.parentElement !== maxParent) {
    return getNextElementSibling(node.parentElement);
  }
  return null;
}

/**
 * Get the next element sibling of the element until the sibling is editable.
 * @param element
 * @param maxParent
 */
export function getPreviousEditableElementSibling(
  element: Node | HTMLElement,
  maxParent: HTMLElement | null = null
) {
  let previousElement = getPreviousElementSibling(element, maxParent);
  while (
    previousElement &&
    (!("isContentEditable" in previousElement) ||
      previousElement.isContentEditable === false)
  ) {
    previousElement = getPreviousElementSibling(previousElement, maxParent);
  }
  return previousElement;
}

/**
 * @param node
 * @param maxParent
 */
export function getPreviousElementSibling(
  node: Node | HTMLElement,
  maxParent: HTMLElement | null = null
): null | Element | HTMLElement {
  if (isTextNode(node)) {
    return node.parentElement?.previousElementSibling || null;
  }
  if ("previousElementSibling" in node && node.previousElementSibling) {
    return node.previousElementSibling;
  }
  if (node.parentElement !== maxParent && node.parentElement != null) {
    return getPreviousElementSibling(node.parentElement);
  }
  return null;
}

/**
 * Set the caret approximately to the position of the last caret.
 * @param rangeBefore the last caret position
 * @param inputElement
 */
export function repositionCaret(
  rangeBefore:
    | {
        startContainer: Node;
        endContainer: Node;
        startOffset: number;
        endOffset: number;
      }
    | RangeCopy,
  inputElement: HTMLElement
) {
  if (!rangeBefore) {
    console.warn("no range before", rangeBefore);
    return;
  }
  const input = inputElement;

  const refreshed =
    "refreshed" in rangeBefore ? rangeBefore.refreshed(input) : null;

  const newRange = document.createRange();
  if (rangeBefore.startContainer == input) {
    // set to end of input if selection is in now deleted part.
    if (rangeBefore.startOffset >= input.childNodes.length) {
      const lastNode =
        input.lastChild?.childNodes[input.lastChild.childNodes.length - 1] ??
        input.lastChild;
      if (lastNode) {
        newRange.setStart(lastNode, lastNode.textContent?.length || 0);
        newRange.setEnd(lastNode, lastNode.textContent?.length || 0);
      }
    } else {
      const spanAtOffset = input.childNodes[rangeBefore.startOffset];
      const spanChild = spanAtOffset.childNodes?.[0] ?? spanAtOffset;
      newRange.setStart(spanChild, 0);
      newRange.setEnd(spanChild, 0);
    }
  } else if (refreshed && input.contains(refreshed?.startContainer)) {
    if (
      isTextNode(refreshed.startContainer) &&
      refreshed.startOffset > refreshed.startContainer.length
    ) {
      newRange.setStart(
        refreshed.startContainer,
        refreshed.startContainer.length
      );
    } else {
      newRange.setStart(refreshed.startContainer, refreshed.startOffset);
    }

    if (input.contains(refreshed?.endContainer)) {
      if (
        isTextNode(refreshed.endContainer) &&
        refreshed.endOffset > refreshed.endContainer.length
      ) {
        newRange.setEnd(refreshed.endContainer, refreshed.endContainer.length);
      } else {
        newRange.setEnd(refreshed.endContainer, refreshed.endOffset);
      }
    } else {
      newRange.setEnd(refreshed.startContainer, refreshed.startOffset);
    }
  } else {
    const lastNode = input.lastChild?.childNodes?.[0] ?? input.lastChild;
    if (lastNode) {
      newRange.setStart(lastNode, lastNode.textContent?.length || 0);
      newRange.setEnd(lastNode, lastNode.textContent?.length || 0);
    }
  }

  // console.debug("reposition range", refreshed, rangeBefore, newRange);

  // Restore selection:
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(newRange);
  }
}

/**
 * @param inputElement
 */
export function setCaretToStart(inputElement: Element | HTMLElement) {
  const input = inputElement;
  const newRange = document.createRange();
  newRange.setStart(input, 0);
  newRange.setEnd(input, 0);
  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(newRange);
  }
  if ("focus" in inputElement) {
    inputElement.focus();
  }
  return newRange;
}

/**
 * Move the caret to the end of the input.
 * @param inputElement
 */
export function setCaretToEnd(inputElement: Node | HTMLInputElement) {
  if (inputElement == null) return;
  const newRange = document.createRange();
  newRange.selectNodeContents(inputElement);
  newRange.collapse(false);

  if ("focus" in inputElement) {
    inputElement.focus();
  }

  const sel = window.getSelection();
  if (sel) {
    sel.removeAllRanges();
    sel.addRange(newRange);
  }
  return newRange;
}

export function isTextNode(node: Node): node is Text {
  return node.nodeType === Node.TEXT_NODE;
}

export function insertCaretAtPoint(x: number, y: number, selectCallback: () => void = () => {}) {
  let textNode = null;
  let offset = 0;

  if (document.caretPositionFromPoint) {
    const position = document.caretPositionFromPoint(
      x,
      y,
    );
    if (position == null) {
      selectCallback();
      return;
    }

    textNode = position.offsetNode;
    offset = position.offset;

    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      const range = document.createRange();
      range.setStart(textNode, offset);
      range.collapse(true);
      selection.addRange(range);
    } else {
      selectCallback();
    }
  } else if (document.caretRangeFromPoint) {
    // Use WebKit-proprietary fallback method
    const range = document.caretRangeFromPoint(x, y);
    if (range == null) {
      selectCallback();
      return;
    }
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
      range.collapse(true);
      selection.addRange(range);
    } else {
      selectCallback();
    }
  } else {
    selectCallback();
  }
}
